import { api } from "#/lib/api-client";
import type { CardField, CardFieldPage, CardTemplate } from "./types";

const BASE = "/api/business-cards";

export const businessCardApi = {
  /** GET /api/business-cards/templates */
  async list(): Promise<CardTemplate[]> {
    const { data } = await api.get<CardTemplate[]>(`${BASE}/templates`);
    return data;
  },

  /** GET /api/business-cards/templates/{id} */
  async get(id: number): Promise<CardTemplate> {
    const { data } = await api.get<CardTemplate>(`${BASE}/templates/${id}`);
    return data;
  },

  /**
   * POST /api/business-cards/templates (multipart) — `back` is optional;
   * omit it entirely for a front-only template rather than sending an empty file.
   */
  async upload(name: string, front: File, back?: File): Promise<CardTemplate> {
    const form = new FormData();
    form.append("name", name);
    form.append("front", front);
    if (back) form.append("back", back);
    const { data } = await api.post<CardTemplate>(`${BASE}/templates`, form, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return data;
  },

  /** PUT /api/business-cards/templates/{id}/fields — replaces the whole field set. */
  async saveFields(id: number, fields: CardField[]): Promise<CardTemplate> {
    const { data } = await api.put<CardTemplate>(`${BASE}/templates/${id}/fields`, fields);
    return data;
  },

  /** POST /api/business-cards/templates/{id}/activate */
  async activate(id: number): Promise<CardTemplate> {
    const { data } = await api.post<CardTemplate>(`${BASE}/templates/${id}/activate`);
    return data;
  },

  /**
   * GET /api/business-cards/templates/{id}/preview.png?page=front|back
   * Blank template page, rasterized — the background the field-position
   * picker draws its boxes over. Returns an object URL; call revokeObjectURL
   * on it (or let the browser reclaim it) once the <img> no longer needs it.
   */
  async templatePreviewUrl(id: number, page: CardFieldPage = "front"): Promise<string> {
    const { data } = await api.get(`${BASE}/templates/${id}/preview.png`, {
      params: { page },
      responseType: "blob",
    });
    return window.URL.createObjectURL(data as Blob);
  },

  /** GET /api/business-cards/employees/{employeeId}.pdf */
  async employeeCardPdf(employeeId: number): Promise<Blob> {
    const { data } = await api.get(`${BASE}/employees/${employeeId}.pdf`, {
      responseType: "blob",
    });
    return data;
  },

  /** GET /api/business-cards/employees/{employeeId}.png?page=front|back */
  async employeeCardPng(employeeId: number, page: CardFieldPage = "front"): Promise<Blob> {
    const { data } = await api.get(`${BASE}/employees/${employeeId}.png`, {
      params: { page },
      responseType: "blob",
    });
    return data;
  },

  /** GET /api/business-cards/sheet.pdf — A4, 5 cards/page, every businessCardEnabled employee. */
  async sheetPdf(): Promise<Blob> {
    const { data } = await api.get(`${BASE}/sheet.pdf`, { responseType: "blob" });
    return data;
  },
};

/** Trigger a browser download for an already-fetched Blob via a short-lived in-memory URL. */
export function downloadBlob(blob: Blob, filename: string) {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}
